import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackButton } from '@/components/shared/BackButton';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchFilters,
  createFilter,
  updateFilter,
  type FilterCondition,
  type FilterAction,
} from '../../lib/inboxFiltersApi';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

const FIELDS: { value: FilterCondition['field']; label: string }[] = [
  { value: 'from_contains', label: 'From address' },
  { value: 'to_contains', label: 'To address' },
  { value: 'subject_contains', label: 'Subject' },
  { value: 'body_contains', label: 'Body text' },
  { value: 'has_attachment', label: 'Has attachment' },
];

const OPERATORS: { value: FilterCondition['operator']; label: string }[] = [
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'equals', label: 'equals' },
  { value: 'is_true', label: 'is true' },
];

const ACTIONS: { value: FilterAction['type']; label: string; needsValue: boolean }[] = [
  { value: 'apply_label', label: 'Apply label', needsValue: true },
  { value: 'mark_read', label: 'Mark as read', needsValue: false },
  { value: 'archive', label: 'Archive', needsValue: false },
  { value: 'add_follow_up', label: 'Add follow-up flag', needsValue: false },
  { value: 'add_billing_opp', label: 'Flag billing opportunity', needsValue: false },
  { value: 'link_to_matter', label: 'Link to matter (manual review)', needsValue: false },
];

const emptyCondition = (): FilterCondition => ({
  field: 'from_contains',
  operator: 'contains',
  value: '',
});

const emptyAction = (): FilterAction => ({ type: 'mark_read' });

export default function InboxFilterBuilderPage() {
  const navigate = useNavigate();
  const { filterId } = useParams<{ filterId?: string }>();
  const isEdit = !!filterId;
  const qc = useQueryClient();

  const { data: existingRules = [], isLoading: loadingExisting, isError: existingFailed, refetch } = useQuery({
    queryKey: ['inbox-filters'],
    queryFn: fetchFilters,
    enabled: isEdit,
  });

  const existing = isEdit ? existingRules.find((r) => r.id === filterId) : undefined;

  const [name, setName] = useState(existing?.name ?? '');
  const [conditionLogic, setConditionLogic] = useState<'all' | 'any'>(
    existing?.conditionLogic ?? 'all',
  );
  const [conditions, setConditions] = useState<FilterCondition[]>(
    existing?.conditions ?? [emptyCondition()],
  );
  const [actions, setActions] = useState<FilterAction[]>(
    existing?.actions ?? [emptyAction()],
  );
  const [stopProcessing, setStopProcessing] = useState(existing?.stopProcessing ?? false);
  const [error, setError] = useState('');
  // The state above is seeded from `existing` on the FIRST render, when the rule has not been
  // fetched yet — so "Edit rule" opened a blank builder every time, and saving it wrote that
  // blank over the stored rule (name cleared, conditions and actions replaced by one empty row).
  // The two sibling editors (signatures, templates) have always synced in an effect; this one
  // never did.
  useEffect(() => {
    if (!existing) return;
    setName(existing.name);
    setConditionLogic(existing.conditionLogic ?? 'all');
    setConditions(existing.conditions?.length ? existing.conditions : [emptyCondition()]);
    setActions(existing.actions?.length ? existing.actions : [emptyAction()]);
    setStopProcessing(existing.stopProcessing ?? false);
  }, [existing]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = { name, conditionLogic, conditions, actions, stopProcessing, enabled: existing?.enabled ?? true };
      if (isEdit && filterId) return updateFilter(filterId, payload);
      return createFilter(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox-filters'] });
      navigate('/barrister/inbox/settings/filters');
    },
    onError: () => setError('Failed to save filter rule.'),
  });

  function updateCondition(i: number, patch: Partial<FilterCondition>) {
    setConditions((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }

  function updateAction(i: number, patch: Partial<FilterAction>) {
    setActions((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } as FilterAction : a)));
  }

  function handleSave() {
    if (!name.trim()) { setError('Name is required.'); return; }
    if (conditions.some((c) => c.field !== 'has_attachment' && !c.value.trim())) {
      setError('All conditions need a value.'); return;
    }
    setError('');
    saveMutation.mutate();
  }

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--border-strong)', borderRadius: 6,
    padding: '10px 12px', fontSize: 16, color: 'var(--text-primary)',
    fontFamily: "'IBM Plex Sans', sans-serif",
    outline: 'none',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    background: 'var(--surface-card)', cursor: 'pointer',
  };

  /* One label treatment for the whole form. "When" used to be the only inline label, which is
     what made the form read as a loose pile of controls rather than a sequence of fields. */
  const labelStyle: React.CSSProperties = {
    fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
    display: 'block', marginBottom: 6,
  };

  /* Mirrors the shared <Card> used on the other settings pages (header strip, then content),
     expressed inline because this page is not on the Tailwind primitives. */
  const cardStyle: React.CSSProperties = {
    border: '1px solid var(--border-default)', borderRadius: 8,
    background: 'var(--surface-card)', overflow: 'hidden',
  };

  const cardHeaderStyle: React.CSSProperties = {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
    padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--surface)',
  };

  const cardTitleStyle: React.CSSProperties = {
    fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
  };

  const cardBodyStyle: React.CSSProperties = {
    display: 'flex', flexDirection: 'column', gap: 10, padding: 16,
  };

  const conditionBlockStyle: React.CSSProperties = {
    display: 'flex', flexDirection: 'column', gap: 8,
    padding: 12, borderRadius: 6,
    border: '1px solid var(--border-subtle)', background: 'var(--surface)',
  };

  const addButtonStyle: React.CSSProperties = {
    background: 'none', border: '1px dashed var(--border-strong)',
    borderRadius: 6, padding: '11px 14px', cursor: 'pointer',
    fontSize: 14, fontWeight: 500, color: 'var(--text-primary)',
    width: '100%', fontFamily: "'IBM Plex Sans', sans-serif",
  };

  const removeButtonStyle: React.CSSProperties = {
    background: 'none', border: '1px solid var(--border-default)',
    borderRadius: 6, cursor: 'pointer', color: 'var(--text-muted)',
    fontSize: 14, padding: '10px 14px', flexShrink: 0,
  };

  return (
    <div style={{
      maxWidth: 680, margin: '0 auto',
      padding: 'clamp(20px, 5vw, 40px) clamp(14px, 4vw, 24px)',
      fontFamily: "'IBM Plex Sans', sans-serif",
    }}>
      {/* Was an unlabelled arrow wired to a fixed destination. These pages are opened from the
          inbox, from the Email settings hub and from Settings, so a fixed target sent people
          somewhere they had never been. Back means back — same control as every other page. */}
      <BackButton onClick={() => navigate(-1)} />

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          {isEdit ? 'Edit filter rule' : 'New filter rule'}
        </h1>
      </div>

      {/* Editing, but the rule could not be fetched: the builder would otherwise sit blank under
          the heading "Edit rule", and a save would write that blank over the stored rule. */}
      {isEdit && existingFailed ? (
        <QueryErrorState what="this filter rule" onRetry={() => void refetch()} />
      ) : isEdit && loadingExisting ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {/* Name */}
          <div>
            <label style={labelStyle}>Rule name</label>
            <input
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Solicitor correspondence"
            />
          </div>

          {/* Conditions. Each condition is its own block: with two or more of them, a flat list of
              dropdowns gives no clue where one condition ends and the next begins. */}
          <section style={cardStyle}>
            <div style={cardHeaderStyle}>
              <span style={cardTitleStyle}>If</span>
              {/* all/any decides whether an email must meet EVERY condition or just one
                  (`inbox.filters.service.ts` matchesRule: every() vs some()). Over a single
                  condition both answer identically, so the control does nothing until there is a
                  second one — showing it on an empty form made the most prominent thing on the
                  card the one thing that could not change the rule. */}
              {conditions.length > 1 && (
                <select
                  value={conditionLogic}
                  onChange={(e) => setConditionLogic(e.target.value as 'all' | 'any')}
                  style={{ ...selectStyle, flex: '1 1 200px' }}
                >
                  <option value="all">all of these conditions match</option>
                  <option value="any">any of these conditions match</option>
                </select>
              )}
            </div>

            <div style={cardBodyStyle}>
              {conditions.map((c, i) => (
                <div key={i} style={conditionBlockStyle}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <select
                      value={c.field}
                      onChange={(e) => updateCondition(i, { field: e.target.value as FilterCondition['field'], value: '' })}
                      style={{ ...selectStyle, flex: '1 1 150px' }}
                    >
                      {FIELDS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                    {c.field !== 'has_attachment' && (
                      <select
                        value={c.operator}
                        onChange={(e) => updateCondition(i, { operator: e.target.value as FilterCondition['operator'] })}
                        style={{ ...selectStyle, flex: '1 1 130px' }}
                      >
                        {OPERATORS.filter((o) => o.value !== 'is_true').map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    )}
                    {conditions.length > 1 && (
                      <button
                        onClick={() => setConditions((prev) => prev.filter((_, idx) => idx !== i))}
                        aria-label={`Remove condition ${i + 1}`}
                        style={removeButtonStyle}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {c.field !== 'has_attachment' && (
                    <input
                      style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                      value={c.value}
                      onChange={(e) => updateCondition(i, { value: e.target.value })}
                      placeholder="value"
                    />
                  )}
                </div>
              ))}

              <button
                onClick={() => setConditions((prev) => [...prev, emptyCondition()])}
                style={addButtonStyle}
              >
                + Add condition
              </button>
            </div>
          </section>

          {/* Actions */}
          <section style={cardStyle}>
            <div style={cardHeaderStyle}>
              <span style={cardTitleStyle}>Then</span>
            </div>

            <div style={cardBodyStyle}>
              {actions.map((a, i) => {
                const meta = ACTIONS.find((x) => x.value === a.type);
                return (
                  <div key={i} style={conditionBlockStyle}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <select
                        value={a.type}
                        onChange={(e) => updateAction(i, { type: e.target.value as FilterAction['type'], value: undefined })}
                        style={{ ...selectStyle, flex: '1 1 180px' }}
                      >
                        {ACTIONS.map((act) => (
                          <option key={act.value} value={act.value}>{act.label}</option>
                        ))}
                      </select>
                      {actions.length > 1 && (
                        <button
                          onClick={() => setActions((prev) => prev.filter((_, idx) => idx !== i))}
                          aria-label={`Remove action ${i + 1}`}
                          style={removeButtonStyle}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    {meta?.needsValue && (
                      <input
                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                        value={a.value ?? ''}
                        onChange={(e) => updateAction(i, { value: e.target.value })}
                        placeholder="e.g. label name"
                      />
                    )}
                  </div>
                );
              })}

              <button
                onClick={() => setActions((prev) => [...prev, emptyAction()])}
                style={addButtonStyle}
              >
                + Add action
              </button>
            </div>
          </section>

          {/* Stop processing */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={stopProcessing}
              onChange={(e) => setStopProcessing(e.target.checked)}
              style={{ width: 18, height: 18, flexShrink: 0 }}
            />
            <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>
              Stop processing further rules if this rule matches
            </span>
          </label>

          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 6, padding: '8px 14px', fontSize: 13, color: '#dc2626',
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <button
              onClick={handleSave} className="btn-mobile-full"
              disabled={saveMutation.isPending}
              style={{
                padding: '11px 22px', borderRadius: 6, flex: 1, minWidth: 150,
                background: '#1e3a5f', color: 'white',
                border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: 500,
                opacity: saveMutation.isPending ? 0.6 : 1,
              }}
            >
              {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create rule'}
            </button>
            <button
              onClick={() => navigate('/barrister/inbox/settings/filters')} className="btn-mobile-full"
              style={{
                padding: '11px 22px', borderRadius: 6, flex: 1, minWidth: 150,
                background: 'var(--surface-card)', color: 'var(--text-primary)',
                border: '1px solid var(--border-default)', cursor: 'pointer',
                fontSize: 14,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
